import { getFocusableElements } from "@theme/a11y";
import { events, subscribe } from "@theme/event-bus";
import { fetchConfig, json } from "@theme/fetch";
import {
  classes,
  getAttributeValue,
  isFromBreakpoint,
  isUntilBreakpoint,
} from "@theme/helpers";
import { HTMLCustomElement } from "@theme/html-custom-element";

class SiteHeader extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    new ResizeObserver(() => {
      requestAnimationFrame(() => {
        document.body.style.setProperty(
          "--header-height",
          `${this.getBoundingClientRect().height.toFixed(2)}px`,
        );
      });
    }).observe(this);

    const section = this.closest(".site-header");
    if (!section) return;

    const syncScrollBackground = () => {
      const isScrolled = window.scrollY > 0;

      section.style.backgroundColor = isScrolled
        ? "var(--color-background)"
        : "";
      section.classList.toggle("is-scrolled", isScrolled);
    };

    this.on("scroll", window, syncScrollBackground, { passive: true }, true);

    subscribe(events.cartUpdated, this.updateCartIcon.bind(this));
  }

  updateCartIcon() {
    const cartBadge = this.refs.cartBadge;

    try {
      return json(fetch(`${theme.routes.cart_url}.js`)).then(
        ({ item_count }) => {
          console.log("item_count", item_count);
          if (cartBadge) {
            cartBadge.classList.toggle(
              "header-cart-badge--full",
              item_count > 0,
            );
            cartBadge.classList.toggle(
              "header-cart-badge--empty",
              item_count === 0,
            );
          }
        },
      );
    } catch {}
  }
}

class HeaderMenu extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.setDefaultState();

    /**
     * Submenus.
     */
    if (this.refs.link) {
      this.on(
        "click",
        this.refs.link.all,
        (event) =>
          isUntilBreakpoint("medium") && this.handleLinkOpenEvent(event),
      );
      this.on(
        "mouseenter",
        this.refs.link.all,
        (event) =>
          isFromBreakpoint("medium") && this.handleLinkOpenEvent(event),
      );
      this.on(
        "mouseleave",
        this.refs.link.all,
        (event) =>
          isFromBreakpoint("medium") && this.handleLinkCloseEvent(event),
      );
      this.on(
        "focusin",
        this.refs.link.all,
        (event) =>
          isFromBreakpoint("medium") && this.handleLinkOpenEvent(event),
      );
      this.on(
        "focusout",
        this.refs.link.all,
        (event) =>
          isFromBreakpoint("medium") && this.handleLinkCloseEvent(event),
      );
    }
  }

  /**
   * Sets the default open states.
   */
  setDefaultState() {
    /**
     * Close all dropdowns by default.
     */
    // if (this.refs.submenuDropdown) {
    //   for (const dropdown of this.refs.submenuDropdown.all) {
    //     this.openDropdown(dropdown.dataset.block, true);
    //   }
    // }

    this.alignSubmenuLinkListWithParentLink();
  }

  /**
   * Align submenu link list with parent link
   */
  alignSubmenuLinkListWithParentLink() {
    const submenus = this.refs.submenu.all;

    for (const submenu of submenus) {
      const parentItem = submenu.closest(".header-menu__item");
      const parentLink = parentItem.querySelector(".header-menu__link");
      const positionFromLeft = parentLink.getBoundingClientRect().left;
      submenu.style.paddingInlineStart = `${positionFromLeft}px`;
    }
  }

  /**
   * Handles the top level link mouse event, to open it's submenu.
   * @param {MouseEvent} event
   */
  handleLinkOpenEvent(event) {
    console.log("handleLinkOpenEvent", event.type);
    const submenu =
      this.refs.submenu &&
      this.refs.submenu.all.find(
        (submenu) => submenu.dataset.block === event.target.dataset.block,
      );

    console.log("submenu", submenu);

    if (submenu) event.preventDefault();

    if (isUntilBreakpoint("medium")) {
      let menuItem = event.target.closest(".header-menu__item");
      if (menuItem.classList.contains(classes.open)) {
        this.closeSubmenu(menuItem.dataset.block);
      } else {
        this.openSubmenu(menuItem.dataset.block);
      }
    } else {
      this.openSubmenu(event.target.dataset.block);
    }
  }

  /**
   * Handles the top level link mouse event, to close it's submenu.
   * @param {MouseEvent} event
   */
  handleLinkCloseEvent(event) {
    console.log("handleLinkCloseEvent", event.type);
    const submenu =
      this.refs.submenu &&
      this.refs.submenu.all.find(
        (submenu) => submenu.dataset.block === event.target.dataset.block,
      );

    if (submenu) event.preventDefault();

    this.closeSubmenu(event.target.dataset.block);
  }

  /**
   * Handles the submenu item mouse event, to open it's dropdown.
   * @param {MouseEvent} event
   * @param {boolean} close - Closes the submenu dropdown.
   */
  handleSubmenuLinkEvent(event, close = false) {
    const dropdown =
      this.refs.submenuDropdown &&
      this.refs.submenuDropdown.all.find(
        (submenu) =>
          submenu.dataset.block === event.currentTarget.dataset.block,
      );

    if (dropdown) event.preventDefault();

    this.openDropdown(event.currentTarget.dataset.block, close);
  }

  /**
   * Opens a submenu.
   * @param {string} id
   */
  openSubmenu(id) {
    /**
     * Go through each top-level link and set the active state.
     */
    if (this.refs.link) {
      for (const link of this.refs.link.all) {
        if (link.dataset.block === id) {
          link.classList.add(classes.open);
        }
      }
    }
  }

  /**
   * Closes a submenu.
   * @param {string} id
   */
  closeSubmenu(id) {
    /**
     * Go through each top-level link and set the active state.
     */
    if (this.refs.link) {
      for (const link of this.refs.link.all) {
        if (link.dataset.block === id) {
          link.classList.remove(classes.open);
        }
      }
    }
  }

  /**
   * Opens a drropdown.
   * @param {string} id
   * @param {boolean} close
   */
  openDropdown(id, close = false) {
    /**
     * Go through each submenu link and set the active state.
     */
    if (this.refs.submenuLink) {
      for (const link of this.refs.submenuLink.all) {
        link.classList.toggle(
          classes.open,
          !close && link.dataset.block === id,
        );
      }
    }

    if (this.refs.submenuDropdown) {
      /**
       * Go through each dropdown and either close or open it.
       */
      for (const dropdown of this.refs.submenuDropdown.all) {
        const isOpen = !close && dropdown.dataset.block === id;

        dropdown.classList.toggle(classes.open, isOpen);

        for (const focusable of getFocusableElements(dropdown)) {
          focusable.tabIndex = isOpen ? 0 : -1;
        }
      }
    }
  }
}

class HeaderAnnouncements extends HTMLCustomElement {
  activeIndex = 0;

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    // Disable rotation for ticker mode - announcements now scroll continuously via CSS
    // The ticker effect is handled by the announcement-bar CSS animations

    // Show all announcements (remove rotation)
    for (const element of Array.from(this.children)) {
      element.classList.add(classes.active);
    }
  }

  /**
   * Shows the provided index item.
   * @param {number} index - The item to show.
   * @param {boolean} immediate - Disables any transitions.
   */
  showIndex(index, immediate = false) {
    for (const [elementIndex, element] of Array.from(this.children).entries()) {
      let transition;
      const isActive = elementIndex === index;

      if (immediate) {
        transition = element.style.transition;

        element.style.transition = "none";
        element.offsetHeight;
      }

      element.classList.toggle(classes.active, isActive);

      if (immediate) {
        requestAnimationFrame(() => {
          element.style.transition = transition;
        });
      }
    }

    this.activeIndex = index;
  }
}

if (!customElements.get("header-menu"))
  customElements.define("header-menu", HeaderMenu);
if (!customElements.get("site-header"))
  customElements.define("site-header", SiteHeader);
if (!customElements.get("header-announcements"))
  customElements.define("header-announcements", HeaderAnnouncements);
